import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { lookupQuery } from '@/queries/lookup';

/**
 * Brand options for the station brand select, sorted by name with "Others" last.
 * Data: the `brands` lookup (`lookupQuery`), loaded once per session and shared by every caller.
 * Constraint: the saved lookup rows are shared, so the hook sorts a COPY (an in-place sort would
 * reorder the rows for every other caller).
 * Edge case: with no data yet, a `selected` brand still shows as the only option, so a saved value
 * does not render empty while loading.
 *
 * @param selected Brand key already chosen, kept visible until the list loads.
 */
export const useBrands = (selected?: string) => {
  const { data, isLoading } = useQuery(lookupQuery({ category: 'brands' }));

  const options = useMemo(() => {
    if (data) {
      // Sort a copy: `data` is the saved copy shared by every caller and must not change.
      return [...data]
        .sort((a, b) => {
          if (a.key === 'OTHERS') {
            return 1;
          }

          if (b.key === 'OTHERS') {
            return -1;
          }

          return a.value > b.value ? 1 : -1;
        })
        .map(({ key, value, isActive }) => ({
          value: key,
          label: value,
          disabled: !isActive,
        }));
    }

    if (selected) {
      return [{ value: selected, label: selected }];
    }

    return [];
  }, [data, selected]);

  return {
    options,
    loading: isLoading,
  };
};
